<template>
  <span aria-hidden="true" />
</template>

<script lang="ts" setup>
  import { useHead } from '@unhead/vue';
  import { addMinutes, format } from 'date-fns';
  import { computed, ref, onMounted } from 'vue';

  import { getExperienceList, getSights } from '@/shared/api/generated/api';
  import type { RuntimeExperience, RuntimeExperienceListResponse, RuntimeSightsResponse } from '@/shared/models/api';
  import { randFloat, randInt } from '@/shared/utils/random';
  import { withoutDomain } from '@/shared/utils/url';

  const props = defineProps<{
    citySlug: string | undefined;
    seo?: { h1?: string; h2?: string } | null;
  }>();

  const experiences = ref<RuntimeExperience[]>([]);
  const sightThumb = ref<string>();

  onMounted(async () => {
    if (!props.citySlug) {
      return;
    }

    const [expRes, sightsRes] = await Promise.all([
      getExperienceList({ query: { city__slug: props.citySlug, limit: 6, offset: 0 } }).catch(() => ({
        data: undefined,
      })),
      getSights({ query: { city_slug: props.citySlug, page_size: 1, sorting: '-excursions_count' } }).catch(() => ({
        data: undefined,
      })),
    ]);

    const experienceData = expRes.data as RuntimeExperienceListResponse | undefined;
    const sightsData = sightsRes.data as RuntimeSightsResponse | undefined;

    experiences.value = experienceData?.results ?? [];
    sightThumb.value = sightsData?.results?.[0]?.cover_photo?.thumbnail;
  });

  const cityUrl = computed(() => (props.citySlug ? `/experience/${props.citySlug}/` : ''));

  const startDate = (exp: RuntimeExperience) => {
    const firstScheduleItem = (exp as RuntimeExperience & { schedule?: { upcoming_events?: string[] } }).schedule
      ?.upcoming_events?.[0];

    if (!firstScheduleItem) {
      return format(new Date(), "yyyy-MM-dd'T'HH:mm");
    }

    return format(new Date(firstScheduleItem), "yyyy-MM-dd'T'HH:mm");
  };

  const endDate = (exp: RuntimeExperience) => {
    const firstScheduleItem = (exp as RuntimeExperience & { schedule?: { upcoming_events?: string[] } }).schedule
      ?.upcoming_events?.[0];

    if (firstScheduleItem) {
      const duration = (exp.duration ?? 1) * 60;
      const timeEnd = addMinutes(new Date(firstScheduleItem), duration);

      return format(timeEnd, "yyyy-MM-dd'T'kk:mm");
    }

    return 0;
  };

  const expList = computed(() => experiences.value);

  const schemaEvent = computed(() =>
    [...expList.value].slice(0, 4).map((exp) => ({
      '@context': 'http://schema.org',
      '@type': 'Event',
      description: exp.tagline,
      endDate: endDate(exp),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      eventStatus: 'http://schema.org/EventScheduled',
      image: exp.photos?.[0]?.thumbnail,
      location: exp.geo?.city?.[0]
        ? [
            {
              '@type': 'Place',
              address: [
                {
                  '@type': 'PostalAddress',
                  addressCountry: [
                    {
                      '@type': 'Country',
                      name: exp.geo.country?.[0]?.name ?? null,
                    },
                  ],
                  addressLocality: exp.geo.city[0].name,
                  streetAddress: exp.meeting_point ?? null,
                },
              ],
              name: exp.geo.city[0].name,
              url: cityUrl.value,
            },
          ]
        : null,
      name: `🧭${exp.title}`,
      offers: [
        {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          price: exp.price?.value_string?.match(/[0-9/.]+/),
          priceCurrency: exp.price?.currency,
          url: exp.url,
          validFrom: startDate(exp),
        },
      ],
      organizer: [
        {
          '@type': 'Organization',
          name: exp.guide?.first_name,
          url: withoutDomain(exp.guide?.url ?? ''),
        },
      ],
      performer: [
        {
          '@type': 'PerformingGroup',
          name: exp.guide?.first_name,
          url: withoutDomain(exp.guide?.url ?? ''),
        },
      ],
      startDate: startDate(exp),
      url: exp.url,
    })),
  );

  const schemaProduct = computed(() =>
    [...expList.value].slice(0, 4).map((exp) => ({
      '@context': 'http://schema.org',
      '@type': 'product',
      aggregateRating: [
        {
          '@type': 'http://schema.org/AggregateRating',
          ratingValue: exp.rating || randFloat(4.5, 5),
          reviewCount: exp.review_count || randInt(5, 100),
        },
      ],
      description: exp.tagline,
      image: exp.photos?.[0]?.thumbnail,
      name: exp.title,
      offers: [
        {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          price: exp.price?.value_string?.match(/[0-9/.]+/),
          priceCurrency: exp.price?.currency,
          url: exp.url,
          validFrom: startDate(exp),
        },
      ],
      url: exp.url,
    })),
  );

  const jsonLd = computed(() => {
    const first = expList.value[0];
    const end = first ? endDate(first) : 0;

    return {
      '@context': 'https://schema.org',
      headline: props.seo?.h1 || '',
      description: props.seo?.h2 || '',
      datePublished: first ? startDate(first) : '',
      dateModified: end === 0 ? '' : end,
      image: sightThumb.value || '',
      author: [
        {
          '@type': 'Person',
          name: 'Tripster',
        },
      ],
    };
  });

  useHead({
    script: computed(() => [
      ...schemaEvent.value.map((obj, i) => ({
        key: `sights-schema-event-${String(i)}`,
        type: 'application/ld+json' as const,
        innerHTML: JSON.stringify(obj),
      })),
      ...schemaProduct.value.map((obj, i) => ({
        key: `sights-schema-product-${String(i)}`,
        type: 'application/ld+json' as const,
        innerHTML: JSON.stringify(obj),
      })),
      {
        key: 'sights-schema-page',
        type: 'application/ld+json' as const,
        innerHTML: JSON.stringify(jsonLd.value),
      },
    ]),
  });
</script>
